import { useEffect, useRef, useState } from "react";

import { Capacitor } from "@capacitor/core";

import { Broadcaster } from "@nav-games/multiplayer-ble";

interface BroadcastToggleProps {
  /// Module slug stamped onto every envelope so the listener
  /// picks the right renderer. e.g. "morse" | "semaphore" | "flags".
  module: string;
  /// Current message text — pushed to listeners every time it changes
  /// (debounced lightly so a fast typist doesn't saturate the
  /// 180-byte broadcast budget).
  text: string;
}

/// Reusable "📡 Broadcast" widget used by every Translate page.
/// Native-only (BLE plugin can't broadcast from a browser); web
/// users see a notice pointing to the mobile app.
export function BroadcastToggle({ module, text }: BroadcastToggleProps) {
  const native = Capacitor.isNativePlatform();
  const [active, setActive] = useState(false);
  const [audience, setAudience] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const broadcasterRef = useRef<Broadcaster | null>(null);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      const b = broadcasterRef.current;
      if (b) void b.stop();
      broadcasterRef.current = null;
    };
  }, []);

  // When active, push every text change to listeners. A trailing
  // debounce keeps a fast typist from spamming the radio — 250 ms
  // feels right (slow enough to coalesce, fast enough that the
  // class sees a sentence land before you start the next).
  useEffect(() => {
    if (!active || !broadcasterRef.current) return;
    const handle = window.setTimeout(() => {
      void broadcasterRef.current?.send({ module, text });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [active, module, text]);

  const startBroadcast = async () => {
    setError(null);
    const b = new Broadcaster();
    try {
      await b.start((count) => setAudience(count));
      broadcasterRef.current = b;
      setActive(true);
      // Send the current text immediately so a listener who joins
      // *before* the teacher edits sees something.
      await b.send({ module, text });
    } catch (e) {
      setError((e as Error).message);
      try {
        await b.stop();
      } catch {
        /* idempotent */
      }
    }
  };

  const stopBroadcast = async () => {
    setActive(false);
    setAudience(0);
    const b = broadcasterRef.current;
    if (b) {
      try {
        await b.stop();
      } catch {
        /* idempotent */
      }
    }
    broadcasterRef.current = null;
  };

  if (!native) {
    return (
      <div className="card">
        <p className="small muted">
          📡 Broadcast (teach a group of phones over Bluetooth) is mobile-only —
          available in the iOS and Android apps.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 style={{ marginBottom: 4 }}>📡 Broadcast</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        {active
          ? `Listeners: ${audience}. Students open "Listen to a broadcast" on their own phone to join.`
          : "Stream your typed message to every phone nearby that's joined a broadcast — for teaching."}
      </p>
      {active ? (
        <button className="secondary" onClick={stopBroadcast}>
          Stop broadcasting
        </button>
      ) : (
        <button onClick={startBroadcast}>Start broadcasting</button>
      )}
      {error && (
        <p className="small" style={{ color: "var(--danger)", marginTop: 8 }}>
          {error}
        </p>
      )}
    </div>
  );
}
